import axiosInstance from '../utils/axios';

/**
 * Streaks, XP and badges (#11) plus the score trend across a series (#12).
 *
 * Both are free and both are pure aggregates over activity the student already generated —
 * nothing here is a separate stored record that could drift from the answers behind it.
 *
 * Note the deliberate nulls: `improvementPct` is null after a single attempt rather than
 * 0, because one point is not a trend and "0%" would read as "no improvement" instead of
 * "not enough data yet". Same for a skill's `masteryPct` before it has been attempted.
 */

export interface TrendPoint {
  sessionId: string;
  packageId: string | null;
  series: string | null;
  subjects: string[] | null;
  score: number | null;
  totalScore: number | null;
  /** Score as a percentage of totalScore. Null when totalScore is 0 or absent. */
  percentage: number | null;
  takenAt: string;
}

export interface SubjectTrend {
  subject: string;
  attempts: number;
  firstPercentage: number | null;
  latestPercentage: number | null;
  deltaPct: number | null;
}

export interface ProgressTrend {
  studentId: string;
  /** Null when the caller asked for every series rather than one. */
  series: string | null;
  /** Attempts oldest-first, ready to plot. */
  points: TrendPoint[];
  bySubject: SubjectTrend[];
  /** Null with fewer than two attempts — see the note above. */
  improvementPct: number | null;
  callout: string | null;
}

export interface Badge {
  key: string;
  title: string;
  description: string;
  earned: boolean;
  /** Current value toward the threshold, so a progress bar needs no extra call. */
  progress: number;
  threshold: number;
}

export interface ProgressSummary {
  studentId: string;
  /** Consecutive days ending today or yesterday — yesterday still counts. */
  currentStreakDays: number;
  longestStreakDays: number;
  activeDaysTotal: number;
  xp: number;
  questionsAttempted: number;
  questionsCorrect: number;
  accuracyPct: number | null;
  sessionsCompleted: number;
  badges: Badge[];
  /** Nearest unearned badge, so the UI has something to aim at. Null once all are earned. */
  nextBadge: Badge | null;
}

export const fetchProgressSummary = async (
  studentId: string
): Promise<ProgressSummary | null> => {
  try {
    const response = await axiosInstance.get(`/students/${studentId}/progress-summary`);
    if (response.data?.status === 'success' && response.data?.data) {
      return response.data.data as ProgressSummary;
    }
    return null;
  } catch {
    return null;
  }
};

export const fetchProgressTrend = async (
  studentId: string,
  series?: string
): Promise<ProgressTrend | null> => {
  try {
    const response = await axiosInstance.get(`/students/${studentId}/trend`, {
      params: series ? { series } : undefined
    });
    if (response.data?.status === 'success' && response.data?.data) {
      return response.data.data as ProgressTrend;
    }
    return null;
  } catch {
    return null;
  }
};
